import { CustomDataType, CustomOutputContainerType, TaskConfigType } from 'Types/Tasks.types'
import { IContainerStrategy } from '../ContainerStrategy'
import { ContainerDataType, ContainerType, TaskMode } from 'Types/Tasks.types'
import { cloneDeep, isEmpty } from 'lodash'
import { CustomTask } from 'Tasks/CustomTask/CustomTask'
import { isViewerMode } from 'Utils/task.utils'

export class CustomContainerStrategy
    implements IContainerStrategy<ContainerType.Custom, CustomDataType>
{
    containerContext?: {
        task: CustomTask
    }

    setContext(containerContext: { task: CustomTask }) {
        this.containerContext = containerContext
    }

    getEmptyDraftContainer(): ContainerDataType<ContainerType.Custom, CustomDataType> {
        return {
            data: {
                schema_version: 1,
                type: ContainerType.Custom,
                data: {
                    response: {
                        id: crypto.randomUUID(),
                        internal_data: {},
                        is_complete: false,
                        extra_info: {},
                        version_id: null,
                        main_info: {} as CustomOutputContainerType,
                    },
                    quality_check: {
                        evaluation_on_response_version_id: {},
                    },
                },
            },
        }
    }

    getContainerFromOutputStart(
        outputStartContainer: ContainerDataType<ContainerType.Custom, CustomDataType>,
        mode: TaskMode
    ): ContainerDataType<ContainerType.Custom, CustomDataType> {
        const draftContainer = cloneDeep(outputStartContainer)

        if (draftContainer && !isViewerMode(mode)) {
            draftContainer.data.data.quality_check.evaluation_on_response_version_id = {}
            draftContainer.decision = null
        }

        return draftContainer
    }

    getModifiedDraftOutputContainer = (
        draftOutputContainer: ContainerDataType<ContainerType.Custom, CustomDataType>,
        _: TaskConfigType<ContainerType.Custom, CustomDataType>
    ) => {
        const draftContainer = cloneDeep(draftOutputContainer)

        if (!draftContainer) {
            return draftContainer
        }

        const task = this.containerContext?.task
        if (!task) {
            return draftContainer
        }

        if (isEmpty(draftContainer.data.data.response.main_info)) {
            draftContainer.data.data.response.main_info = {
                value: {
                    turns: [],
                    final_short_answer: '',
                    external_model_response: undefined,
                    external_model_name: undefined,
                    prompt_annotation: {
                        difficulty: '',
                        complexity: '',
                        accuracy: '',
                        domain: '',
                        sub_domain: '',
                        time_for_strategy: 0,
                        time_to_solve: 0,
                        human_final_answer: '',
                        human_final_answer_reasoning: '',
                    },
                },
                value_type: 'JSON',
            }
        }
        if (!draftContainer.data.data.response.main_info.value.turns) {
            draftContainer.data.data.response.main_info.value.turns = []
        }

        const generateResponseFormValues = task.getGenerateResponseForm?.()?.getFieldsValue?.()
        const manualResponseFormValues = task.getManualResponseForm?.()?.getFieldsValue?.()
        if (!isEmpty(generateResponseFormValues)) {
            draftContainer.data.data.response.main_info.value.prompt_annotation = {
                difficulty: generateResponseFormValues.difficultyLevel,
                domain: generateResponseFormValues.domain,
                sub_domain: generateResponseFormValues.subDomain,
                time_for_strategy: generateResponseFormValues.timeToDevelopStrategy,
                time_to_solve: generateResponseFormValues.timeToSolve,
                complexity: generateResponseFormValues.complexityLevel,
                accuracy: generateResponseFormValues.accuracy,
                human_final_answer: generateResponseFormValues.humanFinalAnswer,
                human_final_answer_reasoning: generateResponseFormValues.humanFinalAnswerReasoning,
                human_complete_final_answer: generateResponseFormValues.humanCompleteFinalAnswer,
                model_reasoning_trace: generateResponseFormValues.modelReasoningTrace,
                model_reasoning_taken_from: generateResponseFormValues.modelReasoningTakenFrom,
                model_final_answer: generateResponseFormValues.modelFinalAnswer,
                feedback_level_metrics: generateResponseFormValues.modelFeedbackDetails?.map(
                    detail => ({
                        feedback_usefulness: detail.feedbackUsefulness,
                        feedback_result_code: detail.feedbackResultCode,
                        feedback_usefulness_justification: detail.feedbackUsefulnessJustification,
                    })
                ),
            }
        } else if (!isEmpty(manualResponseFormValues)) {
            draftContainer.data.data.response.main_info.value.prompt_annotation = {
                human_complete_final_answer: manualResponseFormValues.humanCompleteFinalAnswer,
                human_final_answer_reasoning: manualResponseFormValues.humanFinalAnswerReasoning,
                model_reasoning_trace: manualResponseFormValues.modelReasoningTrace,
                model_final_answer: manualResponseFormValues.modelFinalAnswer,
                accuracy: manualResponseFormValues.accuracy,
                feedback_level_metrics: manualResponseFormValues.modelFeedbackDetails?.map(
                    detail => ({
                        feedback_usefulness: detail.feedbackUsefulness,
                        feedback_result_code: detail.feedbackResultCode,
                        feedback_usefulness_justification: detail.feedbackUsefulnessJustification,
                    })
                ),
            } as unknown as never
        }

        const primaryResponseForms = task.getPrimaryResponseForms?.() ?? []
        const turnResponseForms = task.getTurnResponseForms?.() ?? []

        draftContainer.data.data.response.main_info.value.turns.forEach((turn, index) => {
            const primaryResponseFormValues = primaryResponseForms[index]?.getFieldsValue?.()
            if (primaryResponseFormValues) {
                turn.annotation_data.response_annotation = {
                    ...turn.annotation_data.response_annotation,
                    accuracy: primaryResponseFormValues.isCorrect ? 'YES' : 'NO',
                    instruction_following: primaryResponseFormValues.followInstructions
                        ? 'YES'
                        : 'NO',
                    skill_check: primaryResponseFormValues.skillsUsed,
                    skill_gaps: primaryResponseFormValues.skillsGap,
                }
            }

            const turnResponseFormValues = turnResponseForms[index]?.getFieldsValue?.()
            if (turnResponseFormValues?.values) {
                turn.annotation_data.steps_annotation = [...turnResponseFormValues.values]
            }
        })

        const humanResponseFormvalues = task.getHumanResponseForm?.()?.getFieldsValue?.()
        if (humanResponseFormvalues) {
            draftContainer.data.data.response.main_info.value.final_short_answer =
                humanResponseFormvalues.human_final_answer

            //Check if human response content is present
            if (humanResponseFormvalues.values) {
                draftContainer.data.data.response.main_info.value.human_correct_response = {
                    steps: humanResponseFormvalues.values.map((value, index) => {
                        return {
                            content: value.correct_step,
                            python_or_text: value.python_or_llm,
                            step_number:
                                draftContainer.data.data.response.main_info.value
                                    .human_correct_response?.steps[index].step_number ?? 0,
                        }
                    }),
                }
            }
        }

        return draftContainer
    }
}
